import {
  forwardRef,
  type HTMLProps,
  type LegacyRef,
  useState,
  useEffect,
} from 'react';
import type {
  ApiReturnSite,
  ApiSite,
  ApiReturnCompany,
  ApiReturnProject,
} from '@highimpact-frontend/api-types';
import 'react-pure-modal/dist/react-pure-modal.min.css';
import { getOrElse, recordNotEqual } from '../../utils/functions';
import CoordianatesPicker from '../utils/coordiantesPicker/CoordianatesPicker';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import SiteService from '../../api/services/config/site/SiteService';
import type { Point } from '../../utils/geom/types';
import { resetExternalState } from '../../store/data/slice';
import { useAppDispatch } from '../../store/hooks';
import CompanyService from '../../api/services/config/company/CompanyService';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import DropdownItemsSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import PermissionDecoratorV2 from '../../components/utils/permission/PermissionDecoratorV2';
import TimezoneSelector from './TimezoneSelector';
import ProjectService from '../../api/services/config/project/ProjectService';
import DropDownMultiAutocompleteComponent from '../utils/MUISpecifics/dropDownMultiSelector/dropDownAutocompleteMultiSelector';
import UserService from '../../api/services/config/user/UserService';
import UserSiteService from '../../api/services/config/user_site/UserSiteService';

// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  onCancel: () => void;
  //open: boolean;
  site: ApiReturnSite | null;
  projectSites: number[];
  isModalOpen: boolean;
}

// Component props.
export interface FormPropsNotNull extends FormProps {
  site: ApiReturnSite;
}

function siteNotNull(
  props: FormProps | FormPropsNotNull
): props is FormPropsNotNull {
  return Boolean(props.site);
}

function EditSiteFormOrNothing(props: FormProps) {
  if (!siteNotNull(props)) return <></>;
  else {
    return <EditSiteForm {...props} />;
  }
}

// Component.
function EditSiteForm(
  {
    projectSites,
    isModalOpen,
    site,
    children,
    onCancel,
    ...props
  }: FormPropsNotNull,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // use translation here :
  const { t } = useTranslation('components.sites');

  const dispatch = useAppDispatch();
  const { submit, state } = useSubmit();
  const [projects, setProjects] = useState<ApiReturnProject[]>([]);
  const [companies, setCompanies] = useState<ApiReturnCompany[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    projectSites[0] ?? null
  );
  const [projectUsers, setProjectUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [initialUserIds, setInitialUserIds] = useState<number[]>([]);

  const defaultValues: ApiSite = {
    name: site.name || '',
    surface: site.surface || 0,
    associated_projects_ids: projectSites,
    position: site.position || { type: 'POINT', coordinates: [0, 0] },
    address: site.address || '',
    city: site.city || '',
    postal_code: site.postal_code || null,
    france_weather_station_id: site.france_weather_station_id || null,
    company_id: site.company_id || 0,
    timezone: site.timezone ?? null,
  };

  // Ckeck for changes.

  // Handle form submission.
  const onSubmit = async (data: ApiSite) => {
    submit({
      callbacks: [
        async () => {
          await SiteService.update(site.id, data);
          const initialIds = new Set(initialUserIds);
          const selectedIds = new Set(selectedUsers.map((user) => user.id));
          const usersToAdd = selectedUsers.filter(
            (user) => !initialIds.has(user.id)
          );
          const usersToRemove = initialUserIds.filter(
            (id) => !selectedIds.has(id)
          );
          await Promise.all([
            ...usersToAdd.map((user) =>
              UserSiteService.add({ user_id: user.id, site_id: site.id })
            ),
            ...usersToRemove.map((id) =>
              UserSiteService.delete({ user_id: id, site_id: site.id })
            ),
          ]);
        },
        //methods == useForm.reset(): values = initialValees, submited = false,
        () => methods.reset(),
        () => onCancel(),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => dispatch(resetExternalState({ external_key: 'project_sites' })),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () =>
          dispatch(
            resetExternalState({ external_key: 'device_measure_types' })
          ),
      ],
      errorMessages: {
        anyError: t('Une erreur est survenue lors de la modification du site'),
        unicityError: t('Un site du même nom existe déjà dans ce projet'),
      },
      successMessage: t('Le site a été modifié avec succès'),
    });
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiSite>({
    name: [
      (n) => (n.length <= 3 ? t('Entrer au moins 4 caractères') : undefined),
    ],
    surface: [],
    perimeter: [],
    address: [],
    position: [(n) => (!n ? 'Saisir une position valide' : undefined)],
    associated_projects_ids: [
      (n) =>
        n.length === 0 ? t('Sélectionner au moins un projet') : undefined,
    ],
    company_id: [(n) => (!n ? t('Sélectionner une entreprise') : undefined)],
    france_weather_station_id: [
      (n) => {
        if (n === null || n === undefined || n === '') return undefined;
        const value = String(n);
        return !/^\d{8}$/.test(value)
          ? t('Entrer exactement 8 chiffres ou laisser le champ vide')
          : undefined;
      },
    ],
  });

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);
  const position = methods.watch('position');
  const timezoneCoordinates =
    position?.coordinates != null
      ? {
        lat: Number(position.coordinates[0]),
        lng: Number(position.coordinates[1]),
      }
      : null;

  /**
   * Retrieve coordinates.
   */
  const handleCoordinateSelect = (
    value: { lat: number; lng: number },
    area: number,
    address: Record<string, string> | undefined
  ) => {
    // Créer la chaîne WKT avec la longitude et la latitude
    const point: Point<number, number> = {
      type: 'POINT',
      coordinates: [value.lat, value.lng],
    };

    //Remove falses values in the adress, makes strings valid
    const cleanString = (v?: string) =>
      !v || v === 'undefined' || v === 'null' ? '' : v;

    // Assigner la chaîne WKT à la méthode setValue pour 'perimeter'
    methods.setMultipleValues({
      position: point,
      surface: area,
      address: [cleanString(address?.house_number), cleanString(address?.road)]
        //Leave only “true” values in the array.
        // That is, remove: undefined, null, '', 0, false, NaN
        //makes the array clean
        .filter(Boolean)
        .join(' ')
        .trim(),
      city: cleanString(address?.city ?? address?.town),
      postal_code: address?.postcode ? Number(address.postcode) : null,
    });
  };

  useEffect(() => {
    setIsLoadingCompanies(true);
    CompanyService.getCompanies()
      .then((companies) => {
        setCompanies(companies);
      })
      .catch((error) => {
        console.error('Error fetching companies:', error);
      })
      .finally(() => {
        setIsLoadingCompanies(false);
      });
    ProjectService.getProjects(true)
      .then((projects) => {
        setProjects(projects);
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
      });
    UserService.getUsersBySiteId(site.id)
      .then((users) => {
        const mappedUsers = users.map((user) => ({
          id: user.id,
          name: user.name + ' ' + user.surname,
        }));
        setSelectedUsers(mappedUsers);
        setInitialUserIds(mappedUsers.map((user) => user.id));
      })
      .catch((error) => {
        console.error('Error fetching site users:', error);
      });
    if (selectedProjectId !== null) {
      UserService.getUsersByProjectId(selectedProjectId)
        .then((users) => {
          setProjectUsers(
            users.map((user) => ({ id: user.id, name: user.name + ' ' + user.surname }))
          );
        })
        .catch((error) => {
          console.error('Error fetching project users:', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companiesList = companies.map((company) => ({
    id: company.id,
    name: getOrElse(company.name),
  }));

  const selectedUsersChanged =
    selectedUsers.length !== initialUserIds.length ||
    selectedUsers.some((user) => !initialUserIds.includes(user.id));

  return (
    <div className="customized-dialog-wrapper">
      <CustomizedDialog
        title={t('Modifier le site')}
        open={isModalOpen && isLoadingCompanies === false}
        onCancel={() => {
          onCancel();
          methods.reset();
        }}
      >
        <form
          onSubmit={methods.onSubmit}
          className={`form ${props.className || ''}`}
        >
          <Stack spacing={2}>
            <TextInput
              onChange={(value) => {
                methods.setValue('name', value);
              }}
              label={t('Nom du site')}
              defaultValue={methods.watch('name')}
              name={'name'}
              placeholder="Entrer un nom"
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
            />
            <DropdownItemsSelector
              onSelect={async (selectedId) => {
                setSelectedProjectId(selectedId);
                methods.setValue(
                  'associated_projects_ids',
                  selectedId ? [selectedId] : []
                );
                if (selectedId === null) {
                  setProjectUsers([]);
                  setSelectedUsers([]);
                  return;
                }
                try {
                  const users = await UserService.getUsersByProjectId(
                    selectedId
                  );
                  const mappedUsers = users.map((user) => ({
                    id: user.id,
                    name: user.name + ' ' + user.surname,
                  }));
                  setProjectUsers(mappedUsers);
                  setSelectedUsers(mappedUsers);
                } catch (error) {
                  console.error('Error fetching users:', error);
                  setProjectUsers([]);
                  setSelectedUsers([]);
                }
              }}
              values={projects.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              defaultValue={
                Object.values(methods.watch('associated_projects_ids'))[0]
              }
              label={t('Projet')}
              disabled={false}
              name={'project_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                    tag: 'Submited',
                    errors: methods.getErrors('associated_projects_ids'),
                  }
                  : { tag: 'Not submitted' }
              }
            />
            <DropDownMultiAutocompleteComponent
              label={t('Utilisateurs associés au site')}
              options={projectUsers}
              value={selectedUsers}
              onChange={setSelectedUsers}
              disabled={selectedProjectId === null}
            />
            <DropdownItemsSelector
              onSelect={(selectedId) => {
                methods.setValue('company_id', selectedId);
              }}
              values={companiesList}
              label={t("Nom de l'exploitant")}
              defaultValue={methods.watch('company_id')}
              disabled={false}
              name={'company_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                    tag: 'Submited',
                    errors: methods.getErrors('company_id'),
                  }
                  : { tag: 'Not submitted' }
              }
            />
            <PermissionDecoratorV2 item="france_station_input">
              <TextInput
                onChange={(value) => {
                  methods.setValue('france_weather_station_id', value);
                }}
                label={t('Identifiant de la station Météo France')}
                name={'id_france_weather_station'}
                disabled={true}
                placeholder="Entrer un identifiant"
                defaultValue={
                  methods.watch('france_weather_station_id')
                    ? String(methods.watch('france_weather_station_id'))
                    : ''
                }
                helperText={t(
                  "Si votre site n'est pas concerné par les données Météo France, ne renseignez pas le champ Identifiant de la station Météo France"
                )}
                helperVariant="warning"
                errors={
                  methods.hasSubmitted
                    ? {
                      tag: 'Submited',
                      errors: methods.getErrors(
                        'france_weather_station_id'
                      ),
                    }
                    : { tag: 'Not submitted' }
                }
              />
            </PermissionDecoratorV2>
            <CoordianatesPicker
              label={t('Position du site')}
              onClick={(value, area, address) =>
                handleCoordinateSelect(value, area, address)
              }
              context={t('du site')}
              errors={
                methods.hasSubmitted
                  ? {
                    tag: 'Submited',
                    errors: methods.getErrors('position'),
                  }
                  : { tag: 'Not submitted' }
              }
              required
              defaultValues={
                methods.watch('position')?.coordinates
                  ? {
                    position: {
                      lat: Number(
                        methods.watch('position')?.coordinates[0]
                      ),
                      lng: Number(
                        methods.watch('position')?.coordinates[1]
                      ),
                    },
                    addressData: methods.watch('address')
                      ? {
                        road: methods.watch('address'),
                        city: methods.watch('city'),
                        postcode: methods.watch('postal_code'),
                        house_number: '',
                      }
                      : undefined,
                  }
                  : { position: { lat: 0, lng: 0 }, addressData: undefined }
              }
            />
            <TimezoneSelector
              value={methods.watch('timezone')}
              onSelect={(timezone) => methods.setValue('timezone', timezone)}
              coordinates={timezoneCoordinates}
              label={t('Fuseau horaire')}
            />
            <Button
              type="submit"
              variant="outlined"
              className="form-button-submit"
              disabled={
                !recordNotEqual(defaultValues, methods.watchAll()) &&
                !selectedUsersChanged
              }
              loadingPosition="end"
              data-testid="submit-edited-site-button"
              endIcon={<SaveIcon />}
            >
              {' '}
              {state === 'Loading' ? t('Envoi...') : t('Modifier')}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(EditSiteFormOrNothing);
